import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Ayuda from '../ui/Ayuda';
import { codigoPostal as buscarCP } from './geografia';

/* =====================================================================
   CINCO DIGITOS Y SALE EL RESTO
   =====================================================================
   ES LO QUE CONVIERTE CUATRO CAMPOS EN UNO. Quien llena una dirección
   escribe el código postal y el estado, el municipio y la lista de
   colonias salen solos: cuatro sitios donde equivocarse pasan a ser uno.

   `onResolver` recibe { estado, municipio, colonias, clave_sat } cuando
   el código se encuentra. La pantalla decide qué hace con eso — llenar
   sus campos, o no. Este componente NO escribe en nada que no sea suyo.

   =====================================================================
   MIENTRAS NO ESTE CARGADO EL CATALOGO, ES EL CAMPO DE SIEMPRE
   =====================================================================
   El mismo trato que el selector de municipio: se teclean los cinco
   dígitos y se guardan, que es lo que pasa hoy. Lo único que cambia es
   que si el catálogo está, además se llena lo demás.

   Y NO SE DICE «ESE CODIGO POSTAL NO EXISTE» cuando lo que pasa es que
   nadie ha cargado el archivo. Son dos cosas distintas y el servidor las
   distingue con `cargado`, precisamente para no acusar a la persona de
   un error que no cometió.
   ===================================================================== */
export default function CampoCodigoPostal({
  valor = '',
  onChange,
  onResolver,
  id = 'codigo-postal',
  etiqueta = 'Código postal',
  requerido = false,
  deshabilitado = false,
}) {
  const [hallazgo, setHallazgo] = useState(null);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    let vivo = true;
    if (!/^\d{5}$/.test(valor)) return undefined;
    /* Un instante de espera: quien teclea «64000» no dispara cinco
       búsquedas, dispara una. */
    const reloj = setTimeout(() => {
      if (vivo) setBuscando(true);
      buscarCP(valor)
        .then((r) => {
          if (!vivo) return;
          setHallazgo(r);
          if (r?.cargado) onResolver?.(r);
        })
        .catch(() => { if (vivo) setHallazgo(null); })
        .finally(() => { if (vivo) setBuscando(false); });
    }, 250);
    return () => { vivo = false; clearTimeout(reloj); };
    // `onResolver` fuera a propósito: una pantalla que la redefine en
    // cada pintado dispararía una búsqueda por tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  const completo = /^\d{5}$/.test(valor);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {etiqueta}{requerido && <span className="text-red-600"> *</span>}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        maxLength={5}
        value={valor}
        disabled={deshabilitado}
        /* SE AUTOCORRIGE MIENTRAS SE ESCRIBE, no al guardar: se tira lo
           que no es un dígito en vez de regañar después. */
        onChange={(e) => onChange?.(e.target.value.replace(/\D/g, '').slice(0, 5))}
        className="w-full rounded-lg border px-3 py-2 text-base min-h-11"
        placeholder="5 dígitos"
      />

      <Ayuda id="importador.codigo_postal" />

      {buscando && (
        <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          Buscando…
        </p>
      )}

      {completo && !buscando && hallazgo?.cargado && (
        <p className="mt-1 text-xs text-green-700">
          {hallazgo.municipio}, {hallazgo.estado} ·{' '}
          {hallazgo.asentamientos.length} colonia
          {hallazgo.asentamientos.length === 1 ? '' : 's'}
        </p>
      )}

      {completo && !buscando && hallazgo && !hallazgo.cargado && (
        <p className="mt-1 text-xs text-gray-600">
          El catálogo de códigos postales no está cargado en este servidor.
          El código se guarda igual; el estado y el municipio se escriben.
        </p>
      )}
    </div>
  );
}
